import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Loader2, Sparkles, Mic, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useVoiceSearch } from "@/hooks/useVoiceSearch";
import { cn } from "@/lib/utils";
import { getFriendlyErrorMessage } from "@/lib/errors";

type SearchResult = {
  answer: string;
  totals?: Record<string, number>;
};

interface CollapsibleSearchIAProps {
  inline?: boolean;
}

export function CollapsibleSearchIA({ inline = false }: CollapsibleSearchIAProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);

  const {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
  } = useVoiceSearch({
    onTranscriptionComplete: (text) => {
      console.log('Transcrição recebida:', text);
      setSearchQuery(text);
      setTimeout(() => {
        handleSearch(text);
      }, 500);
    },
    onError: (err) => {
      console.error('Erro de transcrição:', err);
      toast.error(err);
    }
  });

  const handleVoiceToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
      toast.info("🎤 Gravando... fale sua pergunta (máx. 10 segundos)");
    }
  };

  useEffect(() => {
    if (isTranscribing) {
      toast.info("🔄 Processando sua fala...");
    }
  }, [isTranscribing]);

  const exampleQueries = [
    "Quantos pacientes tenho com diabetes?",
    "Mostre exames de glicose dos últimos 3 meses",
    "Qual o histórico de colesterol do paciente João Silva?"
  ];

  const handleExampleClick = (query: string) => {
    setSearchQuery(query);
    handleSearch(query);
  };

  const handleSearch = async (queryOverride?: string) => {
    const query = queryOverride || searchQuery;

    if (!query.trim()) {
      toast.error("Digite uma pergunta para pesquisar");
      return;
    }

    setIsSearching(true);
    setSearchResults(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Você precisa estar logado");
        return;
      }

      const { data, error } = await supabase.functions.invoke("ai-intelligent-search", {
        body: { query, scope: "all", userId: user.id },
      });

      if (error) {
        console.error("Erro da função:", error);
        throw error;
      }

      // Check if data contains an error response
      if (data && !data.success && data.error) {
        throw new Error(data.error);
      }

      setSearchResults({
        answer: data.answer || data.data?.answer || "Sem resposta",
        totals: data.totals || {},
      });
    } catch (error) {
      console.error("Erro na busca:", error);

      const errorMsg = getFriendlyErrorMessage(error);

      toast.error(errorMsg);
    } finally {
      setIsSearching(false);
    }
  };

  if (inline) {
    return (
      <Card className="border-border/50 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Busca Inteligente</h3>
          </div>

          <div className="flex gap-2">
            <Textarea
              placeholder="Pergunte qualquer coisa..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isSearching && searchQuery.trim()) {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              disabled={isSearching || isRecording}
              rows={2}
              className="flex-1 resize-none min-h-[60px]"
            />
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                onClick={() => handleSearch()}
                disabled={isSearching || !searchQuery.trim() || isRecording}
                className="h-[28px]"
              >
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleVoiceToggle}
                disabled={isSearching || isTranscribing}
                className={cn(
                  "h-[28px]",
                  isRecording && "bg-red-500 text-white hover:bg-red-600"
                )}
                title={isRecording ? "Parar gravação" : "Pesquisar com áudio"}
              >
                <Mic className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Exemplos de pesquisa */}
          {!searchResults && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Exemplos:</p>
              <div className="flex flex-wrap gap-2">
                {exampleQueries.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(example)}
                    disabled={isSearching || isRecording}
                    className="text-xs px-2 py-1 rounded-md bg-primary/5 hover:bg-primary/10 text-primary border border-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {searchResults && (
            <div className="max-h-48 overflow-y-auto space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg border border-border/50 space-y-2">
                <p className="text-xs font-semibold text-primary">Resposta:</p>
                <p className="text-xs leading-relaxed whitespace-pre-wrap">
                  {searchResults.answer}
                </p>
              </div>

              <Button
                onClick={() => {
                  navigate('/app/chat-ia', {
                    state: {
                      initialQuery: searchQuery,
                      initialAnswer: searchResults.answer
                    }
                  });
                }}
                size="sm"
                className="w-full"
                variant="outline"
              >
                <MessageSquare className="mr-2 h-3.5 w-3.5" />
                Iniciar Conversa IA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
}
